import { isUndefined } from 'lodash';
import axios, {
    getRequest,
    patchRequest,
    postRequest,
    updateRequest,
} from 'src/utils/axios';
import logger from 'src/utils/logger';
import parse from 'src/utils/parse';

class PimAPI {
    /* =========================
       GET LIST (READ ALL)
       ========================= */
    async getAll(
        page: number,
        limit: number,
        search?: string,
        columns?: any,
        sort?: string
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            const payload: any = {
                page,
                pageSize: limit,
                search,
                column: columns,
                sort: sort === 'asc' ? '1' : '-1',
            };

            if (!search) delete payload.search;
            if (!columns) {
                delete payload.column;
                delete payload.sort;
            }
            if (!sort) delete payload.sort;

            axios
                .get(`${process.env.REACT_APP_PIM}/items`, { params: payload })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) {
                        resolve(response);
                    }
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    /* =========================
       CREATE
       ========================= */
    async create(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const successFunction = (response) => {
                response = parse(response);
                if (!isUndefined(response?.data)) {
                    resolve(response);
                }
            };

            const errorFunction = (error) => {
                logger(error, 'error');
                reject(error);
            };

            const data = {
                url: `${process.env.REACT_APP_PIM}/item`,
                payload,
                successFunction,
                errorFunction,
            };

            postRequest(data);
        });
    }

    /* =========================
       READ BY ID
       ========================= */
    async getById(id: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const successFunction = (response) => {
                response = parse(response);
                if (!isUndefined(response?.data)) {
                    resolve(response);
                }
            };

            const errorFunction = (error) => {
                logger(error, 'error');
                reject(error);
            };

            const data = {
                url: `${process.env.REACT_APP_PIM}/item/${id}`,
                successFunction,
                errorFunction,
            };

            getRequest(data);
        });
    }

    /* =========================
       UPDATE BY ID (PUT)
       ========================= */
    async updateById(id: string, payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const successFunction = (response) => {
                response = parse(response);
                if (!isUndefined(response?.data)) {
                    resolve(response);
                }
            };

            const errorFunction = (error) => {
                logger(error, 'error');
                reject(error);
            };

            const data = {
                url: `${process.env.REACT_APP_PIM}/item/${id}`,
                payload,
                successFunction,
                errorFunction,
            };

            updateRequest(data);
        });
    }

    /* =========================
       PARTIAL UPDATE (PATCH)
       ========================= */
    async patchById(id: string, payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const successFunction = (response) => {
                response = parse(response);
                if (!isUndefined(response?.data)) {
                    resolve(response);
                }
            };

            const errorFunction = (error) => {
                logger(error, 'error');
                reject(error);
            };

            const data = {
                url: `${process.env.REACT_APP_PIM}/item/${id}`,
                payload,
                successFunction,
                errorFunction,
            };

            patchRequest(data);
        });
    }

    /* =========================
       DELETE
       ========================= */
    async deleteById(id: string): Promise<any> {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_PIM}/item/${id}`
            );
            return parse(response);
        } catch (error) {
            logger(error, 'error');
            throw error;
        }
    }

    /* =========================
       BULK DELETE
       ========================= */
    async bulkDelete(ids: Array<string | number>): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${process.env.REACT_APP_PIM}/item/bulk-delete`, { ids })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) {
                        resolve(response);
                    } else {
                        reject(new Error('Internal Server Error'));
                    }
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    /* =========================
       UPDATE STATUS
       ========================= */
    async updateStatusById(id: string, status: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${process.env.REACT_APP_PIM}/item/${id}/status/${status}`)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) {
                        resolve(response);
                    } else {
                        reject(new Error('Internal Server Error'));
                    }
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    /* =========================
   PRICEBOOK APIs
   ========================= */

    async getPricebooks(params: Record<string, any>): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/priceBooks`, { params })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) {
                        resolve(response);
                    } else {
                        reject(new Error('Internal Server Error'));
                    }
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async getPricebookById(id: string): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/pricebooks/${id}`)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) {
                        resolve(response);
                    } else {
                        reject(new Error('Internal Server Error'));
                    }
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async addPricebook(
        organizationId: string,
        payload: Record<string, any>
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .post(
                    `${process.env.REACT_APP_PIM}/priceBooks/${organizationId}`,
                    payload
                )
                .then(async (response) => {
                    const text = await response.data;
                    resolve({
                        message: text,
                        status: response.status,
                        success: response.status === 201,
                    });
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async updatePricebook(
        id: string,
        payload: Record<string, any>
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${process.env.REACT_APP_PIM}/priceBooks/${id}`, payload)
                .then(async (response) => {
                    const text = await response.data;
                    resolve({
                        message: text,
                        status: response.status,
                        success: response.status === 200,
                    });
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async updatePricebookStatus(
        id: string | number,
        payload: { isActive: boolean }
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${process.env.REACT_APP_PIM}/priceBooks/${id}`, payload)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) {
                        resolve(response);
                    } else {
                        reject(new Error('Internal Server Error'));
                    }
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async uploadPricebookFile(formData: FormData): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${process.env.REACT_APP_PIM}/priceBooks/uploadFile`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .then((response) => {
                    resolve({
                        message: response.data,
                        status: response.status,
                        success: response.status === 201,
                    });
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async downloadPricebookTemplate(): Promise<Blob> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/priceBooks/downloadTemplate`, {
                    responseType: 'blob',
                })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async deletePricebook(id: string | number): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .delete(`${process.env.REACT_APP_PIM}/priceBooks/${id}`)
                .then(async (response) => {
                    const text = await response.data;
                    resolve({
                        message: text,
                        status: response.status,
                        success:
                            response.status === 200 || response.status === 204,
                    });
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    /* ========================================================================
   CATALOGUE / CATEGORY / PRODUCT  APIs  (migrated from RTK slice)
   ======================================================================== */

    /* --------------- Catalogue ---------------- */
    async getCatalogues(params: Record<string, any>): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/catalogues`, { params })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    // async addCatalog(payload: Record<string, any>): Promise<{ message: string; status: number; success: boolean }> {
    //     return new Promise((resolve, reject) => {
    //         axios
    //             .post(`${process.env.REACT_APP_PIM}/catalogues`, payload)
    //             .then(async (response) => {
    //                 const text = await response.text();
    //                 resolve({ message: text, status: response.status, success: response.status === 201 });
    //             })
    //             .catch((error) => {
    //                 logger(error, 'error');
    //                 reject(error);
    //             });
    //     });
    // }

    // async updateCatalog(id: string, payload: Record<string, any>): Promise<{ message: string; status: number; success: boolean }> {
    //     return new Promise((resolve, reject) => {
    //         axios
    //             .patch(`${process.env.REACT_APP_PIM}/catalogues/${id}`, payload)
    //             .then(async (response) => {
    //                 const text = await response.text();
    //                 resolve({ message: text, status: response.status, success: response.status === 200 });
    //             })
    //             .catch((error) => {
    //                 logger(error, 'error');
    //                 reject(error);
    //             });
    //     });
    // }

    async updateStatusCatalogueById(id: string, isActive: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${process.env.REACT_APP_PIM}/catalogues/${id}`, { isActive })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async uploadCatalogueFile(formData: FormData): Promise<{ message: string; status: number; success: boolean }> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${process.env.REACT_APP_PIM}/catalogues/uploadFile`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((response) => resolve({ message: response.data, status: response.status, success: response.status === 201 }))
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async downloadCatalogTemplate(): Promise<Blob> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/catalogues/downloadTemplate`, { responseType: 'blob' })
                .then((response) => resolve(response.data))
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    /* --------------- Category ---------------- */
    async getCategories(params: Record<string, any>): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/category`, { params })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    // async addCategory(payload: Record<string, any>): Promise<{ message: string; status: number; success: boolean }> {
    //     return new Promise((resolve, reject) => {
    //         axios
    //             .post(`${process.env.REACT_APP_PIM}/category`, payload)
    //             .then(async (response) => {
    //                 const text = await response.text();
    //                 resolve({ message: text, status: response.status, success: response.status === 201 });
    //             })
    //             .catch((error) => {
    //                 logger(error, 'error');
    //                 reject(error);
    //             });
    //     });
    // }

    // async updateCategory(id: string, payload: Record<string, any>): Promise<{ message: string; status: number; success: boolean }> {
    //     return new Promise((resolve, reject) => {
    //         axios
    //             .patch(`${process.env.REACT_APP_PIM}/category/${id}`, payload)
    //             .then(async (response) => {
    //                 const text = await response.text();
    //                 resolve({ message: text, status: response.status, success: response.status === 200 });
    //             })
    //             .catch((error) => {
    //                 logger(error, 'error');
    //                 reject(error);
    //             });
    //     });
    // }

    async updateStatusCategoryById(id: string, isActive: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${process.env.REACT_APP_PIM}/category/${id}`, { isActive })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async uploadCategoryFile(formData: FormData, paramsData?: Record<string, any>): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${process.env.REACT_APP_PIM}/category/uploadFile`, formData, {
                    params: paramsData,
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((response) => resolve({ message: response.data, status: response.status, success: response.status === 201 }))
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async getCategoryById(selectedCategoryId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/product_template/${selectedCategoryId}`)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    /* --------------- Product ---------------- */
    async getAllProducts(paramsData: Record<string, any> = {}): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/product`, { params: paramsData })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    /** Grouped catalogue: products by domain + PACKAGES section. */
    async getProductPackageGroup(paramsData: Record<string, any> = {}): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/productPackageGroup`, {
                    params: paramsData,
                })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async getProductsByCategoryId(cateID: string, paramsData: Record<string, any> = {}): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/product/getProductByCategoryId/${cateID}`, { params: paramsData })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async getProductsWithPriceBookPrices(paramsData: Record<string, any> = {}): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/product/productsWithPriceBookPrices`, { params: paramsData })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async getProductHeader(tempid: string): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/product_template/${tempid}`)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async addProduct(payload: any): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${process.env.REACT_APP_PIM}/product`, payload)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async updateProduct(id: string, payload: Record<string, any>): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${process.env.REACT_APP_PIM}/product/${id}`, payload)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async updateStatusProductById(id: string, isActive: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${process.env.REACT_APP_PIM}/product/${id}`, { isActive })
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async uploadProductFile(formData: FormData, categoryId?: Record<string, any>): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${process.env.REACT_APP_PIM}/product/uploadProductFile`, formData, {
                    params: categoryId,
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((response) => resolve({ message: response.data, status: response.status, success: response.status === 201 }))
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async getProductHistoryById(productId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/pimlogs/product/${productId}`)
                .then((response) => {
                    response = parse(response);
                    if (!isUndefined(response?.data)) resolve(response);
                    else reject(new Error('Internal Server Error'));
                })
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }

    async downloadProductTemplate(categoryId: string): Promise<Blob> {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.REACT_APP_PIM}/product/downloadTemplate`, {
                    params: { categoryId },
                    responseType: 'blob',
                })
                .then((response) => resolve(response.data))
                .catch((error) => {
                    logger(error, 'error');
                    reject(error);
                });
        });
    }
    /* ==================================================================== */
}

export const pimApi = new PimAPI();
